import { useState, useEffect } from "react"
import {
  Search,
  Filter,
  Download,
  Edit,
  X,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  DollarSign,
  Check,
  Eye,
} from "lucide-react"
import { HRService } from "@/features/hr"
import type { SalesReport } from "@/features/agents"
import type { Organization, Team } from "@/features/organizations"
import type { User, Agent } from "@/features/auth"
import { SalesReportShimmerTable } from "@/shared/components/feedback/Shimmer/ShimmerTable"

interface SalesReportManagerProps {
  onError: (error: string) => void
  selectedCampaign?: string
  organizationId?: string
}

interface FilterState {
  viewBy: "team" | "branch" | "agent" | "organization"
  timeBreakdown: "daily" | "weekly" | "monthly" | "yearly" | "total"
  year?: string
  organizationId?: string
  teamId?: string
  branchId?: string
  agentId?: string
  organizationLevel?: "E" | "D" | "C" | "B" | "A"
  campaignId?: string
  status?: string
  startDate?: string
  endDate?: string
  minAmount?: number
  maxAmount?: number
}

interface EnhancedSalesReport extends SalesReport {
  agentName: string
  teamName: string
  branchName: string
  organizationName: string
  downlineCount: number
  totalDownlineSales: number
  clientName: string
  // Hierarchy columns
  emLevel?: string
  dmLevel?: string
  cmLevel?: string
  bmLevel?: string
  amLevel?: string
}

// Extended types for organizational hierarchy
// interface ExtendedTeam extends Team {
//   branchId?: string
// }

interface ExtendedUser extends User {
  name?: string
  downlineCount?: number
}

type SortField =
  | "agentName"
  | "clientName"
  | "amount"
  | "totalSalesAmount"
  | "salesQuantity"
  | "timePeriod"
  | "status"
  | "reportDate"
  | "organizationName"
  | "teamName"
  | "branchName"
  | "downlineCount"
  | "totalDownlineSales"
  | "emLevel"
  | "dmLevel"
  | "cmLevel"
  | "bmLevel"
  | "amLevel"
type SortDirection = "asc" | "desc"

export default function SalesReportManager({ onError, selectedCampaign, organizationId }: SalesReportManagerProps) {
  const [reports, setReports] = useState<EnhancedSalesReport[]>([])
  const [filteredReports, setFilteredReports] = useState<EnhancedSalesReport[]>([])
  const [paginatedReports, setPaginatedReports] = useState<EnhancedSalesReport[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<FilterState>({
    viewBy: "organization",
    timeBreakdown: "total",
  })
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({
    viewBy: "organization",
    timeBreakdown: "total",
  })
  const [editingReport, setEditingReport] = useState<string | null>(null)
  const [editData, setEditData] = useState<{ status: string }>({ status: "" })
  const [updatingReport, setUpdatingReport] = useState<string | null>(null)
  const [viewingReport, setViewingReport] = useState<EnhancedSalesReport | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showExportDropdown, setShowExportDropdown] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortField, setSortField] = useState<SortField>("reportDate")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(0)

  // Real organizational data from API
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [branches, setBranches] = useState<any[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [campaigns, setCampaigns] = useState<any[]>([])

  const [dataLoadingState, setDataLoadingState] = useState({
    organizations: false,
    agents: false,
    reports: false,
    campaigns: false
  })

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true)
      try {
        await loadOrganizationalData()
      } catch (error) {
        setLoading(false)
        onError(error instanceof Error ? error.message : "Failed to load organizational data")
      }
    }
    initializeData()
  }, [])

  // Set loading to false after organizational data loads if conditions prevent loadReports from being called
  useEffect(() => {
    // If organizational data loading is complete and we can't load reports, stop loading
    if (!dataLoadingState.organizations && !dataLoadingState.agents && !dataLoadingState.reports) {
      if (!organizationId || organizations.length === 0 || agents.length === 0) {
        setLoading(false)
      }
    }
  }, [dataLoadingState, organizationId, organizations.length, agents.length])

  useEffect(() => {
    // Load reports when organizational data is ready, regardless of campaign selection
    // Campaign filter will be applied if selectedCampaign is set
    if (organizations.length > 0 && agents.length > 0) {
      loadReports()
    }
  }, [organizations, agents, selectedCampaign])

  useEffect(() => {
    applyFiltersAndSort()
  }, [reports, filters, searchTerm])

  // Reset to page 1 when filters change (but not when data is loaded)
  useEffect(() => {
    setCurrentPage(1)
  }, [filters, searchTerm])

  // Reload data when sorting, pagination, or items per page changes
  useEffect(() => {
    if (organizations.length > 0 && agents.length > 0) {
      setLoading(true)
      loadReports()
    }
  }, [sortField, sortDirection, currentPage, itemsPerPage])

  // Reload data when viewBy changes
  useEffect(() => {
    if (organizations.length > 0 && agents.length > 0) {
      setLoading(true)
      loadReports()
    }
  }, [filters.viewBy])

  // Handle search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        applyFiltersAndSort()
      }
    }, 500) // Debounce search by 500ms

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Pagination is handled by applyPagination, no need to reload data

  useEffect(() => {
    applyPagination()
  }, [filteredReports, currentPage, itemsPerPage])

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showExportDropdown && !(event.target as Element).closest(".export-dropdown")) {
        setShowExportDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showExportDropdown])

  const loadReports = async () => {
    try {
      setDataLoadingState(prev => ({ ...prev, reports: true }))
      
      // Build filters based on current view and selected filters
      const apiFilters: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortField: sortField,
        sortDirection: sortDirection,
      }

      // CRITICAL: Always enforce HR manager's organizationId - this cannot be overridden
      // This ensures HR managers can ONLY see data from their assigned organization
      if (organizationId) {
        apiFilters.organizationId = organizationId
      }

      // Add campaign filter if selected
      if (selectedCampaign) {
        apiFilters.campaignId = selectedCampaign
      }

      // Add other applied filters
      if (appliedFilters.status) apiFilters.status = appliedFilters.status
      if (appliedFilters.startDate) apiFilters.dateFrom = appliedFilters.startDate
      if (appliedFilters.endDate) apiFilters.dateTo = appliedFilters.endDate
      if (appliedFilters.minAmount !== undefined) apiFilters.minAmount = appliedFilters.minAmount
      if (appliedFilters.maxAmount !== undefined) apiFilters.maxAmount = appliedFilters.maxAmount

      // Add sub-organizational filters (branch, team, agent) ONLY if they belong to HR's organization
      // DO NOT allow organizationId override from filters - HR managers can only filter within their org
      if (appliedFilters.viewBy === "branch" && appliedFilters.branchId) {
        apiFilters.branchId = appliedFilters.branchId
      } else if (appliedFilters.viewBy === "team" && appliedFilters.teamId) {
        apiFilters.teamId = appliedFilters.teamId
      } else if (appliedFilters.viewBy === "agent" && appliedFilters.agentId) {
        apiFilters.agentId = appliedFilters.agentId
      }

      // Call appropriate API based on viewBy
      // Always pass organizationId to ensure data restriction
      let salesData: any
      if (appliedFilters.viewBy === "agent" && appliedFilters.agentId) {
        // When viewing by agent, still enforce organization filter
        salesData = await HRService.getSalesReportsByAgent(appliedFilters.agentId, apiFilters)
      } else {
        // Default: get all sales reports filtered by organization
        salesData = await HRService.getSalesReports(apiFilters, organizationId)
      }

      // Handle new response format
      const sales = salesData.salesReports || []
      const pagination = {
        page: salesData.pagination?.page || 1,
        limit: salesData.pagination?.limit || 50,
        total: salesData.pagination?.total || sales.length,
        pages: salesData.pagination?.totalPages || 1,
      }

      setTotalPages(pagination.pages)
      if (pagination.page !== currentPage) {
        setCurrentPage(pagination.page)
      }

      // Transform sales data to enhanced format
      const enhancedReports: EnhancedSalesReport[] = sales.map((report: SalesReport) => {
        return {
          ...report,
          agentName: report.agent?.user
            ? `${report.agent.user.firstName} ${report.agent.user.lastName}`
            : "",
          teamName: "",
          branchName: "",
          organizationName: report.organization?.name || "",
          downlineCount: report.downlineCountActive + report.downlineCountInactive,
          totalDownlineSales: report.totalDownlineSubmissions,
          clientName: `${report.legalGuardianFullName} / ${report.videoGamerFullName}`,
          campaignName: report.campaign?.name || "",
          salesQuantity: report.salesQuantity || 0,
          emLevel: report.emLevel,
          dmLevel: report.dmLevel,
          cmLevel: report.cmLevel,
          bmLevel: report.bmLevel,
          amLevel: report.amLevel
        }
      })

      setReports(enhancedReports)
    } catch (error: any) {
      if (error.response?.status === 503 && error.response?.data?.error?.includes('Database service temporarily unavailable')) {
        onError('Database service is temporarily unavailable. Please try again in a few moments.')
      } else if (error.message?.includes('Database service temporarily unavailable')) {
        onError('Database service is temporarily unavailable. Please try again in a few moments.')
      } else {
        onError(error instanceof Error ? error.message : "Failed to load sales reports")
      }
    } finally {
      setLoading(false)
      setDataLoadingState(prev => ({ ...prev, reports: false }))
    }
  }


  

  const loadOrganizationalData = async () => {
    try {
      setDataLoadingState(prev => ({ ...prev, organizations: true }))
      // Load organizations (filtered by HR manager's organization)
      const orgs = await HRService.getOrganizations(organizationId)
      setOrganizations(orgs || [])
      setDataLoadingState(prev => ({ ...prev, organizations: false }))

      setDataLoadingState(prev => ({ ...prev, agents: true }))
      // Load agents (filtered by HR manager's organization)
      const agentsData = await HRService.getAgents(undefined, organizationId)
      setAgents(agentsData)
      setDataLoadingState(prev => ({ ...prev, agents: false }))

      // Load campaigns
      try {
        setDataLoadingState(prev => ({ ...prev, campaigns: true }))
        const campaignsData = await HRService.getCampaigns()
        setCampaigns(campaignsData || [])
        setDataLoadingState(prev => ({ ...prev, campaigns: false }))
      } catch (error) {
        console.error("Failed to load campaigns:", error)
        setCampaigns([])
        setDataLoadingState(prev => ({ ...prev, campaigns: false }))
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : "Failed to load organizational data")
      setDataLoadingState({
        organizations: false,
        agents: false,
        reports: false,
        campaigns: false
      })
    }
  }

  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters })
    setCurrentPage(1)
    setLoading(true)
    loadReports()
  }

  const handleClearFilters = () => {
    const defaultFilters: FilterState = {
      viewBy: "organization",
      timeBreakdown: "total",
    }
    setFilters(defaultFilters)
    setAppliedFilters(defaultFilters)
    setCurrentPage(1)
    setLoading(true)
    loadReports()
  }

  const aggregateByTimePeriod = (reports: EnhancedSalesReport[], timeBreakdown: string, year?: string) => {
    const grouped = new Map<string, EnhancedSalesReport[]>()
    
    reports.forEach(report => {
      const reportDate = new Date(report.reportDate)
      
      // Filter by year if specified
      if (year && reportDate.getFullYear().toString() !== year) {
        return
      }
      
      let timeKey: string
      
      switch (timeBreakdown) {
        case 'daily':
          timeKey = `${report.agentId}-${reportDate.toISOString().split('T')[0]}`
          break
        case 'weekly':
          const weekStart = new Date(reportDate)
          weekStart.setDate(reportDate.getDate() - reportDate.getDay())
          timeKey = `${report.agentId}-${weekStart.toISOString().split('T')[0]}`
          break
        case 'monthly':
          timeKey = `${report.agentId}-${reportDate.getFullYear()}-${reportDate.getMonth() + 1}`
          break
        case 'yearly':
          timeKey = `${report.agentId}-${reportDate.getFullYear()}`
          break
        default:
          timeKey = report.agentId
      }
      
      if (!grouped.has(timeKey)) {
        grouped.set(timeKey, [])
      }
      grouped.get(timeKey)!.push(report)
    })
    
    // Aggregate data for each time period
    const aggregated: EnhancedSalesReport[] = []
    
    grouped.forEach((reports, timeKey) => {
      if (reports.length === 0) return
      
      const firstReport = reports[0]
      const aggregatedReport: EnhancedSalesReport = {
        ...firstReport,
        id: `${firstReport.id}-${timeKey}`,
        salesQuantity: reports.reduce((sum, r) => sum + (r.salesQuantity || 0), 0),
        amount: reports.reduce((sum, r) => sum + Number(r.amount), 0),
        totalSubmissions: reports.reduce((sum, r) => sum + r.totalSubmissions, 0),
        acceptedSubmissions: reports.reduce((sum, r) => sum + r.acceptedSubmissions, 0),
        rejectedSubmissions: reports.reduce((sum, r) => sum + r.rejectedSubmissions, 0),
        inProgressSubmissions: reports.reduce((sum, r) => sum + r.inProgressSubmissions, 0),
        downlineCountActive: reports.reduce((sum, r) => sum + r.downlineCountActive, 0),
        downlineCountInactive: reports.reduce((sum, r) => sum + r.downlineCountInactive, 0),
        totalDownlineSubmissions: reports.reduce((sum, r) => sum + r.totalDownlineSubmissions, 0),
        downlineAcceptedSubmissions: reports.reduce((sum, r) => sum + (r.downlineAcceptedSubmissions || 0), 0),
        downlineRejectedSubmissions: reports.reduce((sum, r) => sum + (r.downlineRejectedSubmissions || 0), 0),
        downlineInProgressSubmissions: reports.reduce((sum, r) => sum + (r.downlineInProgressSubmissions || 0), 0),
        // Use the most recent report date for the aggregated period
        reportDate: new Date(Math.max(...reports.map(r => new Date(r.reportDate).getTime()))).toISOString(),
      }
      
      // Recalculate percentages
      const totalSubmissions = aggregatedReport.totalSubmissions
      aggregatedReport.percentageAccepted = totalSubmissions > 0 ? (aggregatedReport.acceptedSubmissions / totalSubmissions) * 100 : 0
      aggregatedReport.percentageInProgress = totalSubmissions > 0 ? (aggregatedReport.inProgressSubmissions / totalSubmissions) * 100 : 0
      aggregatedReport.percentageRejected = totalSubmissions > 0 ? (aggregatedReport.rejectedSubmissions / totalSubmissions) * 100 : 0
      
      const totalDownlineSubmissions = aggregatedReport.totalDownlineSubmissions
      aggregatedReport.averageDownlineSubmissionApproval = totalDownlineSubmissions > 0 ? (aggregatedReport.downlineAcceptedSubmissions / totalDownlineSubmissions) * 100 : 0
      aggregatedReport.averageDownlineSubmissionInProgress = totalDownlineSubmissions > 0 ? (aggregatedReport.downlineInProgressSubmissions / totalDownlineSubmissions) * 100 : 0
      aggregatedReport.averageDownlineSubmissionReject = totalDownlineSubmissions > 0 ? (aggregatedReport.downlineRejectedSubmissions / totalDownlineSubmissions) * 100 : 0
      
      aggregated.push(aggregatedReport)
    })
    
    return aggregated
  }

  const handleViewByChange = (viewBy: "team" | "branch" | "agent" | "organization") => {
    setFilters((prev) => ({
      ...prev,
      viewBy,
      organizationId: "",
      branchId: "",
      teamId: "",
      agentId: "",
    }))
  }

  const applyFiltersAndSort = () => {
    let filtered = [...reports]

    // Apply time breakdown filtering and aggregation
    if (appliedFilters.timeBreakdown && appliedFilters.timeBreakdown !== "total") {
      filtered = aggregateByTimePeriod(filtered, appliedFilters.timeBreakdown, appliedFilters.year)
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (report) =>
          report.agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.legalGuardianFullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.videoGamerFullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.campaignName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.organizationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.teamName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.branchName.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    // Apply status filter
    if (appliedFilters.status) {
      filtered = filtered.filter((report) => report.status === appliedFilters.status)
    }

    // Apply date range filter
    if (appliedFilters.startDate) {
      filtered = filtered.filter((report) => report.reportDate >= appliedFilters.startDate!)
    }
    if (appliedFilters.endDate) {
      filtered = filtered.filter((report) => report.reportDate <= appliedFilters.endDate!)
    }

    // Apply amount filter
    if (appliedFilters.minAmount !== undefined) {
      filtered = filtered.filter((report) => report.amount >= appliedFilters.minAmount!)
    }
    if (appliedFilters.maxAmount !== undefined) {
      filtered = filtered.filter((report) => report.amount <= appliedFilters.maxAmount!)
    }

    // Apply organization level filter (E, D, C, B, A)
    if (appliedFilters.organizationLevel) {
      filtered = filtered.filter((report) => report.organizationType === appliedFilters.organizationLevel)
    }


    // Apply campaign filter
    if (appliedFilters.campaignId) {
      filtered = filtered.filter((report) => report.campaignName === appliedFilters.campaignId)
    }

    // Apply organizational filters
    if (appliedFilters.organizationId) {
      filtered = filtered.filter((report) => {
        const agent = agents.find((a) => a.id === report.agentId)
        // const team = teams.find((t) => t.id === agent?.teamId)
        // const branch = branches.find((b) => b.id === team?.branchId)
        return agent?.organizationId === appliedFilters.organizationId
      })
    }

    if (appliedFilters.branchId) {
      filtered = filtered.filter((report) => {
        const agent = agents.find((a) => a.id === report.agentId)
        // const team = teams.find((t) => t.id === agent?.teamId)
        return agent?.organizationId === appliedFilters.branchId
      })
    }

    if (appliedFilters.teamId) {
      filtered = filtered.filter((report) => {
        const agent = agents.find((a) => a.id === report.agentId)
        return agent?.teamId === appliedFilters.teamId
      })
    }

    if (appliedFilters.agentId) {
      filtered = filtered.filter((report) => report.agentId === appliedFilters.agentId)
    }

    setFilteredReports(filtered)
    // Don't reset page since we're using server-side pagination
  }

  const applyPagination = () => {
    // Since we're using server-side pagination, use filteredReports directly
    setPaginatedReports(filteredReports)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
    setCurrentPage(1) // Reset to first page when sorting changes
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-accent-400" />
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="w-4 h-4 text-primary-600" />
    ) : (
      <ArrowDown className="w-4 h-4 text-primary-600" />
    )
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items)
    setCurrentPage(1)
  }

  const handleEdit = (report: EnhancedSalesReport) => {
    setEditingReport(report.id)
    setEditData({ status: report.status })
  }


  const exportReports = async (format: "csv" | "excel" | "pdf") => {
    try {
      setLoading(true)

      // Build export filters
      const exportFilters: any = {}
      
      // CRITICAL: Always enforce HR manager's organizationId for exports
      // This ensures HR managers can ONLY export data from their assigned organization
      if (organizationId) {
        exportFilters.organizationId = organizationId
      }
      
      if (filters.organizationLevel) exportFilters.organizationLevel = filters.organizationLevel
      if (filters.campaignId || selectedCampaign) exportFilters.campaignId = filters.campaignId || selectedCampaign
      if (filters.status) exportFilters.status = filters.status
      if (filters.startDate) exportFilters.startDate = filters.startDate
      if (filters.endDate) exportFilters.endDate = filters.endDate

      // Add sub-organizational filters (branch, team, agent) ONLY within HR's organization
      // DO NOT allow organizationId override from filters
      if (filters.viewBy === "branch" && filters.branchId) {
        exportFilters.branchId = filters.branchId
      } else if (filters.viewBy === "team" && filters.teamId) {
        exportFilters.teamId = filters.teamId
      } else if (filters.viewBy === "agent" && filters.agentId) {
        exportFilters.agentId = filters.agentId
      }

      // Call export API
      const blob = await HRService.exportSalesReports(exportFilters)

      // Generate filename
      const timestamp = new Date().toISOString().split("T")[0]
      const filename = `sales-reports-${filters.viewBy}-${timestamp}.${format}`

      // Download file
      HRService.downloadFile(blob, filename)

      setShowExportDropdown(false)
    } catch (error) {
      onError(error instanceof Error ? error.message : "Failed to export reports")
    } finally {
      setLoading(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "APPROVED":
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case "REJECTED":
        return <XCircle className="w-4 h-4 text-red-600" />
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />
    }
  }

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full"
    switch (status) {
      case "APPROVED":
        return `${baseClasses} bg-green-100 text-green-800`
      case "REJECTED":
        return `${baseClasses} bg-red-100 text-red-800`
      default:
        return `${baseClasses} bg-yellow-100 text-yellow-800`
    }
  }

  const renderPagination = () => {
    const pages = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-accent-200 sm:px-6">
        <div className="flex justify-between flex-1 sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-accent-700 bg-white border border-accent-300 rounded-md hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-accent-700 bg-white border border-accent-300 rounded-md hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2">
            <p className="text-sm text-accent-700">
              Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{" "}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredReports.length)}</span> of{" "}
              <span className="font-medium">{filteredReports.length}</span> results
            </p>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="ml-4 text-sm border border-accent-300 rounded-md px-2 py-1"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-accent-300 bg-white text-sm font-medium text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 border border-accent-300 bg-white text-sm font-medium text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {pages.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                    page === currentPage
                      ? "z-10 bg-primary-50 border-primary-500 text-primary-600"
                      : "bg-white border-accent-300 text-accent-500 hover:bg-accent-50"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 border border-accent-300 bg-white text-sm font-medium text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-accent-300 bg-white text-sm font-medium text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    )
  }

  const handleSaveEdit = async (reportId: string) => {
    try {
      setUpdatingReport(reportId)
      await HRService.updateSalesReport(reportId, editData)
      setEditingReport(null)
      
      // Update the report in local state instead of reloading all reports
      setReports(prevReports => 
        prevReports.map(report => 
          report.id === reportId 
            ? { ...report, status: editData.status as "PENDING" | "APPROVED" }
            : report
        )
      )
      
      setFilteredReports(prevFilteredReports => 
        prevFilteredReports.map(report => 
          report.id === reportId 
            ? { ...report, status: editData.status as "PENDING" | "APPROVED" }
            : report
        )
      )
      
      setPaginatedReports(prevPaginatedReports => 
        prevPaginatedReports.map(report => 
          report.id === reportId 
            ? { ...report, status: editData.status as "PENDING" | "APPROVED" }
            : report
        )
      )
    } catch (error: any) {
      // Handle database connection errors gracefully
      if (error.response?.status === 503 && error.response?.data?.error?.includes('Database service temporarily unavailable')) {
        onError('Database service is temporarily unavailable. Please try again in a few moments.')
      } else if (error.message?.includes('Database service temporarily unavailable')) {
        onError('Database service is temporarily unavailable. Please try again in a few moments.')
      } else {
        onError(error instanceof Error ? error.message : "Failed to update sales report")
      }
    } finally {
      setUpdatingReport(null)
    }
  }

  const handleCancelEdit = () => {
    setEditingReport(null)
    setEditData({ status: "" })
  }

  const handleViewReport = (reportId: string) => {
    const report = reports.find(r => r.id === reportId)
    if (report) {
      setViewingReport(report)
    }
  }

  const handleCloseView = () => {
    setViewingReport(null)
  }

  
  // Add a function to render appropriate empty states
  const renderEmptyState = () => {
    // NOTE: Don't show loading here - shimmer is shown at table level
    // This function only handles empty data states

    // CRITICAL: Check if HR manager has an assigned organization
    if (!organizationId) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-accent-900 mb-2">No Organization Assigned</h3>
          <p className="text-accent-600 mb-4">
            You are not assigned to any organization. Please contact your administrator to be assigned to an organization before you can view sales reports.
          </p>
        </div>
      )
    }

    // Check if no organizations found (after organizational data is loaded)
    if (organizations.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-accent-900 mb-2">No Organizations Found</h3>
          <p className="text-accent-600 mb-4">
            You don't have access to any organizations. Please contact your administrator.
          </p>
        </div>
      )
    }

    // Check if no agents found in the organization
    if (agents.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-accent-900 mb-2">No Agents Found</h3>
          <p className="text-accent-600 mb-4">
            There are no agents in your organization yet. Agents need to be added before sales reports can be generated.
          </p>
        </div>
      )
    }

    // No reports found with current filters
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-accent-900 mb-2">No Sales Reports Found</h3>
        <p className="text-accent-600 mb-4">
          {searchTerm || Object.keys(appliedFilters).some(key => appliedFilters[key as keyof FilterState] && key !== 'viewBy' && key !== 'timeBreakdown')
            ? "No reports match your current search or filters. Try adjusting your criteria."
            : selectedCampaign 
              ? "There are no sales reports available for the selected campaign in your organization."
              : "There are no sales reports available in your organization yet."}
        </p>
        {(searchTerm || Object.keys(appliedFilters).some(key => appliedFilters[key as keyof FilterState] && key !== 'viewBy' && key !== 'timeBreakdown')) && (
          <button
            onClick={() => {
              setSearchTerm("")
              handleClearFilters()
            }}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <X className="w-4 h-4" />
            <span>Clear All Filters</span>
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-accent-900">Sales Reports</h2>
          <p className="text-accent-600">View and manage sales reports by organizational structure</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative export-dropdown">
            <button
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              className="btn-outline flex items-center justify-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showExportDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-accent-200">
                <div className="py-1">
                  <button
                    onClick={() => {
                      exportReports("csv")
                      setShowExportDropdown(false)
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-accent-700 hover:bg-accent-100"
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={() => {
                      exportReports("excel")
                      setShowExportDropdown(false)
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-accent-700 hover:bg-accent-100"
                  >
                    Export as Excel
                  </button>
                  <button
                    onClick={() => {
                      exportReports("pdf")
                      setShowExportDropdown(false)
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-accent-700 hover:bg-accent-100"
                  >
                    Export as PDF
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-outline flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Enhanced Filter Panel */}
      {showFilters && (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 p-6 mb-6 shadow-lg">
          {/* Filter Header with Icon and Better Typography */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Advanced Filter Options</h3>
                <p className="text-sm text-gray-600">
                  Currently viewing: <span className="font-semibold text-blue-600 capitalize">{filters.viewBy}</span>{" "}
                  level data
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowFilters(false)}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Enhanced Filter Grid with Better Visual Hierarchy */}
          <div className="space-y-6">
            {/* Organizational Level Tabs */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-blue-800">Data View Level</h4>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  Select organizational level
                </span>
              </div>

              {/* Simple Tab Interface */}
              <div className="flex space-x-1 bg-white rounded-lg p-1 border border-blue-200">
                {[
                  { key: "organization", label: "Organization" },
                  { key: "branch", label: "Branch" },
                  { key: "team", label: "Team" },
                  { key: "agent", label: "Agent" },
                ].map((view) => (
                  <button
                    key={view.key}
                    onClick={() => handleViewByChange(view.key as any)}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      filters.viewBy === view.key
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    {view.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time & Period Section */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-gray-800">Time & Period</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time Breakdown</label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                    value={filters.timeBreakdown}
                    onChange={(e) => handleFilterChange("timeBreakdown", e.target.value)}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="total">Total</option>
                  </select>
                </div>

                {filters.timeBreakdown === "yearly" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                    <select
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                      value={filters.year || new Date().getFullYear().toString()}
                      onChange={(e) => handleFilterChange("year", e.target.value)}
                    >
                      {Array.from({ length: 5 }, (_, i) => {
                        const year = new Date().getFullYear() - i
                        return (
                          <option key={year} value={year.toString()}>
                            {year}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Organization & Campaign Section */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-gray-800">Organization & Campaign</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Organization Level</label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                    value={filters.organizationLevel || ""}
                    onChange={(e) => handleFilterChange("organizationLevel", e.target.value)}
                  >
                    <option value="">All Levels</option>
                    <option value="E">E Level</option>
                    <option value="D">D Level</option>
                    <option value="C">C Level</option>
                    <option value="B">B Level</option>
                    <option value="A">A Level</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campaign</label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                    value={filters.campaignId || ""}
                    onChange={(e) => handleFilterChange("campaignId", e.target.value)}
                  >
                    <option value="">All Campaigns</option>
                    {campaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.name}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Amount Filter Section */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-yellow-600" />
                </div>
                <h4 className="text-sm font-semibold text-gray-800">Amount Filter</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Amount</label>
                  <input
                    type="number"
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                    value={filters.minAmount || ""}
                    onChange={(e) => handleFilterChange("minAmount", e.target.value ? Number(e.target.value) : undefined)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Amount</label>
                  <input
                    type="number"
                    placeholder="No limit"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                    value={filters.maxAmount || ""}
                    onChange={(e) => handleFilterChange("maxAmount", e.target.value ? Number(e.target.value) : undefined)}
                  />
                </div>
              </div>
            </div>

            {/* Status & Date Section */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-gray-800">Status & Date Range</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                    value={filters.status || ""}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">From</label>
                      <input
                        type="date"
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.startDate || ""}
                        onChange={(e) => handleFilterChange("startDate", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">To</label>
                      <input
                        type="date"
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.endDate || ""}
                        onChange={(e) => handleFilterChange("endDate", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* View-specific filters with better visual design */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-blue-800">Hierarchical Selection</h4>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  {filters.viewBy.charAt(0).toUpperCase() + filters.viewBy.slice(1)} View
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {filters.viewBy === "organization" && (
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2"> Organization</label>
                    <select
                      className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                      value={filters.organizationId || ""}
                      onChange={(e) => handleFilterChange("organizationId", e.target.value)}
                    >
                      <option value=""> All Organizations</option>
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {filters.viewBy === "branch" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2"> Organization</label>
                      <select
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.organizationId || ""}
                        onChange={(e) => handleFilterChange("organizationId", e.target.value)}
                      >
                        <option value=""> All Organizations</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2"> Branch</label>
                      <select
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.branchId || ""}
                        onChange={(e) => handleFilterChange("branchId", e.target.value)}
                      >
                        <option value=""> All Branches</option>
                        {branches
                          .filter(
                            (branch) => !filters.organizationId || branch.organizationId === filters.organizationId,
                          )
                          .map((branch) => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </>
                )}

                {filters.viewBy === "team" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2"> Organization</label>
                      <select
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.organizationId || ""}
                        onChange={(e) => handleFilterChange("organizationId", e.target.value)}
                      >
                        <option value=""> All Organizations</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2"> Branch</label>
                      <select
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.branchId || ""}
                        onChange={(e) => handleFilterChange("branchId", e.target.value)}
                      >
                        <option value=""> All Branches</option>
                        {branches
                          .filter(
                            (branch) => !filters.organizationId || branch.organizationId === filters.organizationId,
                          )
                          .map((branch) => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2"> Team</label>
                      <select
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.teamId || ""}
                        onChange={(e) => handleFilterChange("teamId", e.target.value)}
                      >
                        <option value=""> All Teams</option>
                        {/* Teams disabled for now */}
                        <option value="">No teams available</option>
                      </select>
                    </div>
                  </>
                )}

                {filters.viewBy === "agent" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2"> Organization</label>
                      <select
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.organizationId || ""}
                        onChange={(e) => handleFilterChange("organizationId", e.target.value)}
                      >
                        <option value=""> All Organizations</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2"> Branch</label>
                      <select
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.branchId || ""}
                        onChange={(e) => handleFilterChange("branchId", e.target.value)}
                      >
                        <option value=""> All Branches</option>
                        {branches
                          .filter(
                            (branch) => !filters.organizationId || branch.organizationId === filters.organizationId,
                          )
                          .map((branch) => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2"> Team</label>
                      <select
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.teamId || ""}
                        onChange={(e) => handleFilterChange("teamId", e.target.value)}
                      >
                        <option value=""> All Teams</option>
                        {/* Teams disabled for now */}
                        <option value="">No teams available</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-2"> Agent</label>
                      <select
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                        value={filters.agentId || ""}
                        onChange={(e) => handleFilterChange("agentId", e.target.value)}
                      >
                        <option value=""> All Agents</option>
                        {agents
                          .filter((agent) => {
                            if (filters.teamId) return agent.teamId === filters.teamId
                            if (filters.branchId) {
                              // const team = teams.find((t) => t.id === agent.teamId)
                              return agent.organizationId === filters.branchId
                            }
                            if (filters.organizationId) {
                              // const team = teams.find((t) => t.id === agent.teamId)
                              // const branch = branches.find((b) => b.id === team?.branchId)
                              return agent.organizationId === filters.organizationId
                            }
                            return true
                          })
                          .map((agent) => (
                            <option key={agent.id} value={agent.id}>
                              {agent.user ? `${agent.user.firstName} ${agent.user.lastName}` : agent.agentCode}
                            </option>
                          ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Enhanced Filter Actions */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    <span className="text-blue-600 font-bold">{filteredReports.length}</span> reports found
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleClearFilters}
                    className="px-6 py-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    <span>Reset Filters</span>
                  </button>
                  <button
                    onClick={handleApplyFilters}
                    className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 border border-transparent rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Apply Filters</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by organization, agent, client, campaign..."
            className="input-field pl-10 pr-20"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Reports Table */}
      <div className="card">
        {loading || dataLoadingState.organizations || dataLoadingState.agents || dataLoadingState.reports ? (
          <SalesReportShimmerTable rows={itemsPerPage} />
        ) : filteredReports.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-accent-200">
                <thead className="bg-accent-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("emLevel")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>EM Level</span>
                        {getSortIcon("emLevel")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("dmLevel")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>DM Level</span>
                        {getSortIcon("dmLevel")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("cmLevel")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>CM Level</span>
                        {getSortIcon("cmLevel")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("bmLevel")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>BM Level</span>
                        {getSortIcon("bmLevel")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("amLevel")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>AM Level</span>
                        {getSortIcon("amLevel")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("agentName")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Agent</span>
                        {getSortIcon("agentName")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("clientName")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Client Names</span>
                        {getSortIcon("clientName")}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("amount")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Submission Quantity</span>
                        {getSortIcon("amount")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("salesQuantity")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Sales Quantity</span>
                        {getSortIcon("salesQuantity")}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Submissions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Downline Agent Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Downline Agent Averages
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {getSortIcon("status")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider cursor-pointer hover:bg-accent-100"
                      onClick={() => handleSort("reportDate")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Date</span>
                        {getSortIcon("reportDate")}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-accent-200">
                  {paginatedReports.map((report) => (
                    <tr key={report.id} className="hover:bg-accent-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold bg-purple-600">
                            E
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            {report.organizationType === 'E' ? report.organizationName : (report.emLevel || "N/A")}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold bg-green-600">
                            D
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            {report.organizationType === 'D' ? report.organizationName : (report.dmLevel || "N/A")}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold bg-yellow-600">
                            C
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            {report.organizationType === 'C' ? report.organizationName : (report.cmLevel || "N/A")}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold bg-orange-600">
                            B
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            {report.organizationType === 'B' ? report.organizationName : (report.bmLevel || "N/A")}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 text-white font-bold bg-red-600">
                            A
                          </div>
                          <div className="text-sm font-medium text-accent-900">
                            {report.organizationType === 'A' ? report.organizationName : (report.amLevel || "N/A")}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-accent-900">
                            {report.agentName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-accent-900">
                          <div className="font-medium">Legal Guardian: {report.legalGuardianFullName}</div>
                          <div className="text-accent-600">Video Gamer: {report.videoGamerFullName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            report.campaignName === "video game addiction"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {report.campaignName}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        <div className="flex items-center space-x-1">
                          <span className="font-bold text-green-700">{report.amount.toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-900">
                        <div className="flex items-center space-x-1">
                          <span className="font-bold text-blue-700">{report.salesQuantity || 0}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-600">
                        <div className="text-xs space-y-1">
                          <div className="flex justify-between">
                            <span className="font-semibold text-accent-800">Total:</span>
                            <span className="text-accent-700">{report.totalSubmissions}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-700">Accepted:</span>
                            <span className="text-green-600 font-medium">{report.acceptedSubmissions}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-red-700">Rejected:</span>
                            <span className="text-red-600 font-medium">{report.rejectedSubmissions}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-yellow-700">In Progress:</span>
                            <span className="text-yellow-600 font-medium">{report.inProgressSubmissions}</span>
                          </div>
                          <div className="border-t pt-1 mt-1">
                            <div className="flex justify-between">
                              <span className="text-green-700">% Accepted:</span>
                              <span className="text-green-600 font-bold">
                                {report.totalSubmissions > 0 ? ((report.acceptedSubmissions / report.totalSubmissions) * 100).toFixed(1) : 0}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-yellow-700">% In Progress:</span>
                              <span className="text-yellow-600 font-bold">
                                {report.totalSubmissions > 0 ? ((report.inProgressSubmissions / report.totalSubmissions) * 100).toFixed(1) : 0}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-red-700">% Rejected:</span>
                              <span className="text-red-600 font-bold">
                                {report.totalSubmissions > 0 ? ((report.rejectedSubmissions / report.totalSubmissions) * 100).toFixed(1) : 0}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-600">
                        <div className="text-xs space-y-1">
                          <div className="flex justify-between">
                            <span className="text-blue-700">Active Agent:</span>
                            <span className="text-blue-600 font-medium">{report.downlineCountActive}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-700">Inactive Agent:</span>
                            <span className="text-gray-600 font-medium">{report.downlineCountInactive}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-semibold text-accent-800">Total Agent:</span>
                            <span className="font-bold text-accent-700">{report.downlineCountActive + report.downlineCountInactive}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-semibold text-accent-800">Total Submissions:</span>
                            <span className="font-bold text-accent-700">{report.totalDownlineSubmissions}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-600">
                        <div className="text-xs space-y-1">
                          <div className="flex justify-between">
                            <span className="text-green-700">Agent Approval:</span>
                            <span className="text-green-600 font-bold">
                              {report.totalDownlineSubmissions > 0 ? ((report.downlineAcceptedSubmissions / report.totalDownlineSubmissions) * 100).toFixed(1) : 0}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-yellow-700">Agent In Progress:</span>
                            <span className="text-yellow-600 font-bold">
                              {report.totalDownlineSubmissions > 0 ? ((report.downlineInProgressSubmissions / report.totalDownlineSubmissions) * 100).toFixed(1) : 0}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-red-700">Agent Reject:</span>
                            <span className="text-red-600 font-bold">
                              {report.totalDownlineSubmissions > 0 ? ((report.downlineRejectedSubmissions / report.totalDownlineSubmissions) * 100).toFixed(1) : 0}%
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingReport === report.id ? (
                          <select
                            className="input-field"
                            value={editData.status}
                            disabled={updatingReport === report.id}
                            onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                          >
                            <option value="PENDING">Pending</option>
                            <option value="APPROVED">Approved</option>
                          </select>
                        ) : (
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(report.status)}
                            <span className={getStatusBadge(report.status)}>{report.status.toLowerCase()}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-600">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(report.reportDate).toLocaleDateString()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {editingReport === report.id ? (
                            <>
                              <button
                                onClick={() => handleSaveEdit(report.id)}
                                disabled={updatingReport === report.id}
                                className="text-green-600 hover:text-green-900 flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {updatingReport === report.id ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                                    <span>Saving...</span>
                                  </>
                                ) : (
                                  <>
                                    <Check className="w-4 h-4" />
                                    <span>Save</span>
                                  </>
                                )}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                disabled={updatingReport === report.id}
                                className="text-gray-600 hover:text-gray-900 flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <X className="w-4 h-4" />
                                <span>Cancel</span>
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEdit(report)}
                                className="text-primary-600 hover:text-primary-900 flex items-center space-x-1"
                              >
                                <Edit className="w-4 h-4" />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleViewReport(report.id)}
                                className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                              >
                                <Eye className="w-4 h-4" />
                                <span>View</span>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredReports.length === 0 && !loading && (
                <div className="text-center py-8">
                  <p className="text-accent-500">No sales reports found matching your criteria.</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {filteredReports.length > 0 && renderPagination()}
          </>
        )}
      </div>

      {/* View Report Modal */}
      {viewingReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-accent-900">Sales Report Details</h3>
              <button
                onClick={handleCloseView}
                className="text-accent-400 hover:text-accent-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-accent-700 mb-3">Report Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-accent-600">Report ID:</span>
                      <span className="font-medium">{viewingReport.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-accent-600">Agent:</span>
                      <span className="font-medium">{viewingReport.agentName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-accent-600">Organization:</span>
                      <span className="font-medium">{viewingReport.organizationType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-accent-600">Campaign:</span>
                      <span className="font-medium">{viewingReport.campaignName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-accent-600">Amount:</span>
                      <span className="font-medium text-green-600">${viewingReport.amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-accent-600">Status:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        viewingReport.status === "APPROVED" 
                          ? "bg-green-100 text-green-800" 
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {viewingReport.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-accent-600">Report Date:</span>
                      <span className="font-medium">{new Date(viewingReport.reportDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-accent-700 mb-3">Client Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-accent-600">Legal Guardian:</span>
                      <span className="font-medium">{viewingReport.legalGuardianFullName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-accent-600">Video Gamer:</span>
                      <span className="font-medium">{viewingReport.videoGamerFullName}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submission Statistics */}
              <div>
                <h4 className="text-sm font-medium text-accent-700 mb-3">Submission Statistics</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{viewingReport.totalSubmissions}</div>
                    <div className="text-sm text-blue-700">Total Submissions</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{viewingReport.acceptedSubmissions}</div>
                    <div className="text-sm text-green-700">Accepted</div>
                    <div className="text-xs text-green-600">
                      {viewingReport.totalSubmissions > 0 ? ((viewingReport.acceptedSubmissions / viewingReport.totalSubmissions) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">{viewingReport.inProgressSubmissions}</div>
                    <div className="text-sm text-yellow-700">In Progress</div>
                    <div className="text-xs text-yellow-600">
                      {viewingReport.totalSubmissions > 0 ? ((viewingReport.inProgressSubmissions / viewingReport.totalSubmissions) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{viewingReport.rejectedSubmissions}</div>
                    <div className="text-sm text-red-700">Rejected</div>
                    <div className="text-xs text-red-600">
                      {viewingReport.totalSubmissions > 0 ? ((viewingReport.rejectedSubmissions / viewingReport.totalSubmissions) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Downline Statistics */}
              <div>
                <h4 className="text-sm font-medium text-accent-700 mb-3">Downline Agent Statistics</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{viewingReport.downlineCountActive}</div>
                    <div className="text-sm text-blue-700">Active Agent</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-gray-600">{viewingReport.downlineCountInactive}</div>
                    <div className="text-sm text-gray-700">Inactive Agent</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{viewingReport.downlineCountActive + viewingReport.downlineCountInactive}</div>
                    <div className="text-sm text-green-700">Total Agent</div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{viewingReport.totalDownlineSubmissions}</div>
                    <div className="text-sm text-purple-700">Total Downline Submissions</div>
                  </div>
                </div>
                
                {viewingReport.totalDownlineSubmissions > 0 && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-lg font-bold text-green-600">
                        {((viewingReport.downlineAcceptedSubmissions / viewingReport.totalDownlineSubmissions) * 100).toFixed(1)}%
                      </div>
                      <div className="text-sm text-green-700">Agent Approval Rate</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="text-lg font-bold text-yellow-600">
                        {((viewingReport.downlineInProgressSubmissions / viewingReport.totalDownlineSubmissions) * 100).toFixed(1)}%
                      </div>
                      <div className="text-sm text-yellow-700">Agent In Progress Rate</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="text-lg font-bold text-red-600">
                        {((viewingReport.downlineRejectedSubmissions / viewingReport.totalDownlineSubmissions) * 100).toFixed(1)}%
                      </div>
                      <div className="text-sm text-red-700">Agent Reject Rate</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end p-6 border-t">
              <button
                onClick={handleCloseView}
                className="px-4 py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
